import { useEffect, useMemo, useState } from 'react';

import { Button } from '@components/button';
import { Checkbox } from '@components/checkbox';
import { UploadedFile } from '@components/file-field';
import { createForm, useCreateFormStore } from '@components/form';
import { HideContainer } from '@components/hide-container';
import { IconDocument } from '@components/icons/document';
import { IconDocumentCreate } from '@components/icons/document-create';
import { IconDynamicField } from '@components/icons/dynamic-field';
import { LabelTextGroup } from '@components/label-text-group';
import { Spinner } from '@components/loading-spinner';
import { usePreviewFileModal } from '@components/modals/preview-file-modal';
import { NoResultCard } from '@components/no-result-card';
import { SelectOption } from '@components/select';
import { InfoButton, Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { VisuallyHidden } from '@components/visually-hidden';
import { useScopedTranslate } from '@locale/translate';
import { getFileExtension } from '@utils/browser';
import { abbreviateDate } from '@utils/date';
import { formatNumber } from '@utils/numbers';
import { toQueryString } from '@utils/requests';
import { fileNameFromPath } from '@utils/strings';

import {
  useDocument,
  useDocumentTemplate,
  useDocumentTemplates,
  useConvertedDocument,
} from 'api/documents/hooks';
import { DocumentTemplateField } from 'api/documents/requests';
import {
  useJourneyTravellerAction,
  useUpdateJourneyTravellerAction,
} from 'api/traveller-actions/hooks';
import { JourneyActionValuesTypes, TravellerJourneyActionDetailed } from 'api/types';
import { useCurrentWorkspace } from 'ui/authenticated/main/helpers';
import { ExecutionMethod } from 'ui/journey-builder/execution-method';
import { DescriptionFormField, useVariableGroups } from 'ui/journey-builder/node-types/common';
import { FormWithUnsavedChangesPrompt } from 'ui/journey-builder/unsaved-changes-prompt';

import { DocumentField, Info, SelectDocument, TemplateCard } from './journey-sidebar';

import { ACTION_TYPES } from '../action-config';
import {
  ActionProgressBar,
  EditableActionContent,
  FormSidebarFooter,
  JourneyActionCard,
  SidebarProps,
  TravellerActionActions,
  TravellerActionFooter,
  TravellerActionHeader,
  TriggerMethod,
  isActionError,
  useEditingContext,
} from '../common';
import { getFieldMappingId } from '../helpers';

const ACTION_TYPE = ACTION_TYPES.create_document;

export const SidebarContent = ({ shortId }: SidebarProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { isEditing } = useEditingContext();
  const { workspaceId } = useCurrentWorkspace();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [idToReplace, setIdToReplace] = useState<string | null>(null);
  const { data, isLoading } = useJourneyTravellerAction<typeof ACTION_TYPE>(shortId);
  const queryString = useMemo(() => {
    return toQueryString({
      workspace: workspaceId,
    });
  }, [workspaceId]);
  const templatesQuery = useDocumentTemplates(queryString);
  const values = data?.values;
  const isReplace = !Boolean(selectedTemplateId) && Boolean(idToReplace);
  const title = Boolean(selectedTemplateId) ? t('title') : t('replaceDocument');

  useEffect(() => {
    if (values?.template?.value) {
      setSelectedTemplateId(values.template.value);
      setIdToReplace(values.template.value);
    }
  }, [values?.template?.value]);

  if (isLoading) {
    return <Spinner />;
  }
  if (!shortId || !data) {
    return <div>{t('dataNotFound')}</div>;
  }
  const action = data;
  const templates = (templatesQuery.data ?? []).filter(
    (template) => template.shortId === selectedTemplateId || template.status === 'active'
  );

  return (
    <Sidebar>
      <TravellerActionHeader actionType={ACTION_TYPE} title={title}>
        {isEditing &&
          (isReplace ? (
            <Button
              variant="ghost"
              text={t('core.cancel')}
              onClick={() => setSelectedTemplateId(idToReplace)}
            />
          ) : (
            <InfoButton>
              <Info />
            </InfoButton>
          ))}
        <TravellerActionActions action={action} />
      </TravellerActionHeader>
      <Sidebar.Content>
        <EditableActionContent
          edit={
            selectedTemplateId ? (
              <FormContent
                action={action}
                selectedTemplateId={selectedTemplateId}
                switchTemplate={() => {
                  setSelectedTemplateId(null);
                }}
              />
            ) : (
              <SelectDocument
                templates={templates}
                onSelect={(id: string) => {
                  setSelectedTemplateId(id);
                  setIdToReplace(id);
                }}
                idToReplace={idToReplace}
                isReplace={isReplace}
              />
            )
          }
          display={<DetailsContent action={action} />}
        />
      </Sidebar.Content>
    </Sidebar>
  );
};

function templateFields(fieldConfig: DocumentTemplateField[], data: any) {
  return fieldConfig.map((field) => {
    return {
      ...field,
      value: data[field.id] || '',
    };
  });
}

type ContentProps = {
  action: TravellerJourneyActionDetailed<typeof ACTION_TYPE>;
};

const DetailsContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const templateId = action.data.template;
  const templateVariables = action.data?.templateVariables;
  const templateQuery = useDocumentTemplate(templateId);

  if (!templateQuery.data?.fieldConfig) {
    return <Spinner />;
  }
  const isError = isActionError(action);

  const fields = templateFields(templateQuery.data?.fieldConfig, templateVariables);
  return (
    <Stack space={6}>
      <ActionProgressBar action={action} isError={isError} isCard={false} />
      <Sidebar.Separator />
      <HideContainer title={t('actionDetails')}>
        <Stack space={4}>
          <LabelTextGroup label={t('description')} text={action.description} />
          <JourneyActionCard title={t('documentFields')} icon={<IconDynamicField />} spaceY={4}>
            <Stack space={4}>
              {fields.map((field) => (
                <LabelTextGroup
                  key={field.id}
                  label={field.label}
                  text={
                    field.type === 'date' && field.value
                      ? abbreviateDate(field.value, 'en')
                      : field.type === 'file' && field.value && typeof field.value === 'object'
                      ? fileNameFromPath(field.value.src) ||
                        fileNameFromPath(field.value.name) ||
                        t('fileSelected')
                      : field.type === 'file' && field.value
                      ? fileNameFromPath(field.value)
                      : (field.type === 'number' || field.type === 'decimal') &&
                        field.value !== null
                      ? formatNumber(field.value)
                      : field.value
                  }
                />
              ))}
            </Stack>
          </JourneyActionCard>
        </Stack>
      </HideContainer>
      <Sidebar.Separator />
      <HideContainer title={t('documentOutput')}>
        {Boolean(action.result) ? (
          <DocumentOutput action={action} />
        ) : (
          <NoResultCard icon={IconDocument} text={t('actionNotRun')} />
        )}
      </HideContainer>
      <TravellerActionFooter
        action={action}
        startIcon={<IconDocumentCreate />}
        startText={t('create')}
        retryText={t('retry')}
      />
    </Stack>
  );
};

const DocumentOutput = ({ action }: { action: ContentProps['action'] }) => {
  const documentQuery = useDocument(action.shortId);
  const convertedDocumentQuery = useConvertedDocument(action.shortId);

  return (
    <Stack space={3}>
      <FileCard file={documentQuery.data?.file} />
      <FileCard file={convertedDocumentQuery.data?.file} />
    </Stack>
  );
};

const FileCard = ({ file }: { file: string }) => {
  const showFilePreviewModal = usePreviewFileModal();

  if (!file) {
    return null;
  }

  const fileName = fileNameFromPath(file);
  const isPdf = getFileExtension(fileName) === 'pdf';

  const onPreview = isPdf
    ? () => showFilePreviewModal({ formFile: { name: fileName, src: file } })
    : undefined;

  return (
    <UploadedFile
      name={fileName}
      path={file}
      iconColor="elementPurpleInvert"
      onPreview={onPreview}
      font="regular14"
    />
  );
};

type CreateDocumentData = {
  template: string;
  description: string;
  isAutomatic: boolean;
  assignees: SelectOption[];
  executionMethod: TriggerMethod;
  selectedTemplateShortId: string;
} & Record<string, string>;

type FormContentProps = ContentProps & {
  selectedTemplateId: string;
  switchTemplate: () => void;
};
const Form = createForm();

const FormContent = ({ action, selectedTemplateId, switchTemplate }: FormContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { stopEditing } = useEditingContext();
  const { workspaceId } = useCurrentWorkspace();
  const queryString = useMemo(() => {
    return toQueryString({
      workspace: workspaceId,
    });
  }, [workspaceId]);
  const { data } = useDocumentTemplates(queryString);
  const fieldMappingId = getFieldMappingId(action.journeyId);
  const formStore = useCreateFormStore();
  const values = action.values as JourneyActionValuesTypes['create_document'];
  const [rename, setRename] = useState(Boolean(values?.name));
  const isFormDirty = formStore.useIsDirty();
  const isDirty = isFormDirty || rename !== Boolean(values?.name);
  const variableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: action.journeyId,
    excludedTypes: ['file', 'file_multi'],
  });
  const emailVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: action.journeyId,
    types: ['email'],
  });
  const phoneVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: action.journeyId,
    types: ['phone'],
  });
  const dateVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: action.journeyId,
    types: ['date'],
  });
  const optionsVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: action.journeyId,
    types: ['select'],
  });
  const fileVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: action.journeyId,
    types: ['file'],
  });
  const numberVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: action.journeyId,
    types: ['number'],
  });
  const decimalVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: action.journeyId,
    types: ['number', 'decimal'],
  });

  const selectedTemplate = data?.find((template) => template.shortId === selectedTemplateId);

  useEffect(() => {
    // Set the currently selected template from the other page into form state
    // This will make it dirty if the template selected isn't the same as previously
    if (selectedTemplate?.shortId) {
      formStore.actions.setValue('selectedTemplateShortId', selectedTemplate.shortId);
    }
  }, [formStore.actions, selectedTemplate?.shortId]);

  const getVariableGroups = (fieldType: string) => {
    switch (fieldType) {
      case 'email':
        return emailVariableGroups;
      case 'phone':
        return phoneVariableGroups;
      case 'date':
        return dateVariableGroups;
      case 'select':
        return optionsVariableGroups;
      case 'file':
        return fileVariableGroups;
      case 'number':
        return numberVariableGroups;
      case 'decimal':
        return decimalVariableGroups;
      default:
        return variableGroups;
    }
  };

  const fields = selectedTemplate?.fieldConfig ?? [];

  const editMutation = useUpdateJourneyTravellerAction(action, {
    onSuccess: stopEditing,
  });

  const extractTemplateVariables = (data: CreateDocumentData) => {
    const prefix = 'templateVariables_';
    const templateVariables: { [key: string]: { value: string; variables: object } } = {};

    Object.keys(data).forEach((key) => {
      if (key.startsWith(prefix)) {
        const newKey = key.substring(prefix.length);
        const value = data[key];
        if (!value) return;
        if (typeof value !== 'object') {
          templateVariables[newKey] = { value, variables: {} };
        } else {
          templateVariables[newKey] = value;
        }
      }
    });

    return templateVariables;
  };

  const handleSubmit = (data: CreateDocumentData) => {
    return editMutation.mutateAsync({
      description: data.description,
      isAutomatic: data.executionMethod === 'automatic',
      assignees: data.executionMethod === 'manual' ? data.assignees.map((user) => user.value) : [],
      values: {
        template: { value: data.selectedTemplateShortId },
        templateVariables: extractTemplateVariables(data),
        name: rename ? data.name : undefined,
      },
    });
  };

  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={(data) => handleSubmit(data)}>
      <VisuallyHidden>
        <Form.TextField
          id="selectedTemplateShortId"
          label="Selected template"
          defaultValue={action.values?.template?.value ?? ''}
        />
      </VisuallyHidden>
      <Stack space={6}>
        <HideContainer title={t('actionDetails')}>
          <Stack space={4}>
            <DescriptionFormField defaultValue={action.description} />
            {selectedTemplate && (
              <TemplateCard selectedTemplate={selectedTemplate} switchTemplate={switchTemplate} />
            )}
          </Stack>
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('triggerMethod')}>
          <ExecutionMethod
            journeyId={action.journeyId}
            assignees={action.assignees}
            isAutomatic={action.isAutomatic}
            hideLabel
            formStore={formStore}
          />
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('documentFields')}>
          <Stack space={4}>
            {fields.map((field) => (
              <DocumentField
                key={field.id}
                field={field}
                defaultValue={action.values?.templateVariables?.[field.id]}
                variableGroups={getVariableGroups(field.type)}
                fieldMappingId={fieldMappingId}
                journeyActionId={action.shortId}
                journeyId={action.journeyId}
              />
            ))}
          </Stack>
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('advanced')} defaultClosed={!rename}>
          <Stack space={4}>
            <Checkbox label={t('renameOutput')} onChange={setRename} defaultValue={rename} />
            {rename && (
              <Form.FieldMappingTextEditor
                id="name"
                plaintext
                singleLine
                label={t('fileOutputName')}
                defaultValue={values?.name}
                variableGroups={variableGroups}
                validators={['required']}
                fieldMappingId={fieldMappingId}
              />
            )}
          </Stack>
        </HideContainer>
      </Stack>
      <FormSidebarFooter Form={Form} isDirty={isDirty} editMutation={editMutation} />
    </FormWithUnsavedChangesPrompt>
  );
};

const sidebarLocale = {
  en: {
    title: 'Create document',
    dataNotFound: 'Data not found',
    description: 'Description',
    automatic: 'Automatic',
    manual: 'Manual',
    details: 'Details',
    file: 'File',
    started: 'Started',
    completed: 'Completed',
    retry: 'Re-convert',
    template: 'Template',
    inProgress: 'In progress',
    error: 'Error',
    notStarted: 'Not started',
    documentCreated: 'Document created',
    actionNotRun: 'Action has not been run',
    createdDocument: 'Created document',
    create: 'Create',
    actionDetails: 'Action details',
    triggerMethod: 'Trigger method',
    documentFields: 'Document fields',
    advanced: 'Advanced',
    renameOutput: 'Rename document output',
    fileOutputName: 'File output name',
    documentOutput: 'Document output',
    replaceDocument: 'Replace document',
    fileSelected: 'File selected',
  },
};
